import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { Eye, EyeOff, CheckCircle, Loader2, Newspaper } from 'lucide-react';
import { auth } from '../contexts/AuthContext.js';

/**
 * Landing page for Firebase Auth action URLs (password reset, email verify).
 * Configured in Firebase Console → Authentication → Templates → Action URL
 * (set to https://app.assayer-ai.com/auth/action).
 *
 * Today we only handle mode=resetPassword. Verify-email is not part of the
 * invite flow (operators set their password via reset link; email is
 * implicitly verified by the reset itself).
 *
 * Ported from packages/vireo-sentinel/vireo-client/src/pages/AuthAction.tsx
 * with assayer color tokens and inline error display.
 */
export function AuthAction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError('Invalid or missing action code.');
        setVerifying(false);
        return;
      }

      if (mode !== 'resetPassword') {
        setError('Unsupported action mode.');
        setVerifying(false);
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
      } catch (err: any) {
        setError(
          err?.code === 'auth/invalid-action-code'
            ? 'This password reset link is invalid or has expired.'
            : 'Failed to verify reset code. Please try again.'
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!oobCode) {
      setError('Invalid action code.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      if (err?.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err?.code === 'auth/invalid-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else {
        setError(err?.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Verifying ─────────────────────────────────────────────────────────
  if (verifying) {
    return (
      <Shell>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-04 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-grey-700 mb-2">Verifying reset link…</h2>
          <p className="text-grey-500">Please wait.</p>
        </div>
      </Shell>
    );
  }

  // ─── Error before form (invalid/expired link) ──────────────────────────
  if (error && !email) {
    return (
      <Shell>
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-6">
          <p className="text-danger-600 text-center">{error}</p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="w-full py-3 px-4 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors"
        >
          Return to Login
        </button>
      </Shell>
    );
  }

  // ─── Success ───────────────────────────────────────────────────────────
  if (success) {
    return (
      <Shell>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-success-50 rounded-full">
            <CheckCircle className="h-8 w-8 text-success-600" />
          </div>
          <h2 className="text-2xl font-bold text-grey-700 mb-2">Password set</h2>
          <p className="text-grey-500 mb-4">You can now sign in with your new password.</p>
          <p className="text-sm text-grey-400">Redirecting to login…</p>
        </div>
      </Shell>
    );
  }

  // ─── Reset form ────────────────────────────────────────────────────────
  return (
    <Shell>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-grey-700">Set Your Password</h1>
        <p className="text-grey-500 mt-2">
          For <strong>{email}</strong>
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg">
          <p className="text-danger-600 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-grey-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 pr-10 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              placeholder="At least 8 characters"
              minLength={8}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-500 hover:text-grey-700"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-grey-700 mb-2">
            Confirm Password
          </label>
          <input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
            placeholder="Re-enter password"
            minLength={8}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Setting password…' : 'Set Password'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <button
          onClick={() => navigate('/login')}
          className="text-sm text-primary-04 hover:text-primary-600 font-medium"
        >
          Back to Login
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-grey-01 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-surface rounded-lg border border-grey-200 p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-primary-50 p-3 rounded-lg mb-4">
            <Newspaper className="h-12 w-12 text-primary-04" />
          </div>
          <h1 className="text-3xl font-bold text-grey-700">Assayer Intelligence</h1>
        </div>
        {children}
      </div>
    </div>
  );
}
